import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';

import { bookingHasDisruption } from '@fcom/common-booking/utils/common-booking.utils';
import { CmsImageData } from '@fcom/core-api/interfaces';
import { BoundType, LocationUtil, SelectedServiceData } from '@fcom/dapi';
import { FinnairOrder } from '@fcom/dapi/api/models';
import { getBookingDestinationSummary } from '@fcom/common/utils/booking.utils';
import { LocationsService } from '@fcom/locations';
import { finShare } from '@fcom/rx';
import { isOrderWithin24HoursOfCreation, ThirdPartyAncillariesProvider } from '@fcom/common/utils';
import { ConfigService } from '@fcom/core/index';

import { Notifications } from '../../../../interfaces';
import { NotificationsService } from '../../../../services/notifications.service';
import { CarTrawlerClientID } from '../../../../constants';

@Component({
  selector: 'fin-mmb-trip',
  templateUrl: './trip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingTripComponent implements OnInit {
  readonly BoundType = BoundType;
  readonly ThirdPartyAncillariesProvider = ThirdPartyAncillariesProvider;

  @Input() booking$: Observable<FinnairOrder>;
  @Input() changeFlightModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @Input() showTravelExtrasButton: boolean;
  @Output() emailModalOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() scrollToTravelExtras: EventEmitter<void> = new EventEmitter<void>();
  @Output() upsellClicked: EventEmitter<SelectedServiceData> = new EventEmitter<SelectedServiceData>();

  summary$: Observable<LocationUtil[]>;
  notifications$: Observable<Notifications>;
  notificationsTop$: Observable<Notifications>;
  image$: Observable<CmsImageData>;
  departureDate$: Observable<string>;
  arrivalDate$: Observable<string>;
  bookingHasNoDisruption$: Observable<boolean>;
  partnerEligibilities$: Observable<Partial<Record<ThirdPartyAncillariesProvider, boolean>>>;
  isWithin24HoursOfCreation$: Observable<boolean>;
  carTrawlerClientId = CarTrawlerClientID.MMB_TRIP_DETAILS_FIRST_24H;
  enableHotelsMMB: boolean;

  constructor(
    private locationsService: LocationsService,
    private notificationsService: NotificationsService,
    private router: Router,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.summary$ = this.booking$.pipe(map((booking) => getBookingDestinationSummary(booking)));
    this.enableHotelsMMB = this.configService.cfg.enableHotelsMMB;
    this.isWithin24HoursOfCreation$ = this.booking$.pipe(map((order) => isOrderWithin24HoursOfCreation(order)));

    this.image$ = this.summary$.pipe(
      map((summary) => summary[1]?.code),
      filter((x) => Boolean(x)),
      distinctUntilChanged(),
      switchMap((code) =>
        this.locationsService.retrieveLocation(code).pipe(
          tap((location) => {
            if (!location) {
              this.locationsService.triggerLocationFetch(code);
            }
          })
        )
      ),
      filter(Boolean),
      map(({ picture }) => picture)
    );

    const dates$ = this.booking$.pipe(
      filter(Boolean),
      map((order) => {
        const departureBound = order.bounds[0]?.disruptedBound?.disruptedBound ?? order.bounds[0];
        const arrivalBound =
          order.bounds[order.bounds.length - 1]?.disruptedBound?.disruptedBound ??
          order.bounds[order.bounds.length - 1];
        return [departureBound.departure.dateTime, arrivalBound.arrival.dateTime];
      }),
      finShare()
    );

    this.departureDate$ = dates$.pipe(map(([departureDate]) => departureDate));
    this.arrivalDate$ = dates$.pipe(map(([, arrivalDate]) => arrivalDate));

    this.notifications$ = this.notificationsService.getNotificationsForTrip(this.router, () =>
      this.openEmailModal(true)
    );
    this.notificationsTop$ = this.notificationsService.getTopNotificationsForTrip();

    this.bookingHasNoDisruption$ = this.booking$.pipe(map((booking) => !bookingHasDisruption(booking)));

    this.partnerEligibilities$ = this.booking$.pipe(
      map((order) => {
        const checkEligibility = (type: 'carRental' | 'airportTransfer' | 'hotel'): boolean =>
          order.eligibilities?.[type]?.find((eligibility) => eligibility.id === 'JOURNEY')?.isAllowedToUse ?? false;

        return {
          [ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL]: checkEligibility('carRental'),
          [ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER]: checkEligibility('airportTransfer'),
          [ThirdPartyAncillariesProvider.EXPEDIA_HOTEL]:
            this.configService.cfg.enableExpediaWidget && checkEligibility('hotel'),
        };
      })
    );
  }

  scrollToTravelExtrasContent(): void {
    this.scrollToTravelExtras.emit();
  }

  openEmailModal(value: boolean): void {
    this.emailModalOpen.emit(value);
  }

  trackByFn(index: number): number {
    return index;
  }
}
